// import type { EnvTag } from "@peerup/auth-kit"

import type { AccountInfo, AuthEvents } from "@peerup/auth-kit"

import { AccountLoginStatus, AccountStatus, AuthEventType } from "@peerup/auth-kit"
import { getParamsFromUrl } from "@peerup/utils"
import { pick } from "lodash-es"

import type { JoinAccount } from "@/types/login.types"

import { i18n } from "@/plugins/i18n"
import router, { getCurrentMeta, getCurrentRoute } from "@/router"
import { $authKit } from "@/store/modules/auth"
import { getUserStore } from "@/store/modules/user"
import { jumpWithRedirect, redirectWithQuery } from "@/utils/url"

import $logger from "./logger"
import { message } from "./message"

/**
 * 处理页签状态的方法，用于保持页签状态同步
 * 这是一个关键方法，用于处理用户在不同页签间的状态同步
 *
 * @param options - 配置选项
 * @param options.status - 账号状态，默认从 authKit 获取当前状态
 * @param options.allowPass - 是否允许通过公共页面的检查，默认为 false
 *
 * 主要逻辑:
 * 1. 获取当前链接 query，判断是否来自"添加更多账号"场景
 * 2. 判断 authKit 是否有登录态
 * 3. 如果有登录态，则跳转到主应用
 * 4. 如果无登录态，则跳转到账号列表或登录页
 */
export const handleTabStatus = async (options?: { status?: AccountStatus; allowPass?: boolean }) => {
  // 初始化配置选项，设置默认值
  options = {
    status: options?.status || $authKit.getStatus(),
    allowPass: options?.allowPass || false,
  }

  // 确保路由实例存在
  await router.isReady() // 等待路由准备好
  const currentRoute = getCurrentRoute()

  // 获取来源参数
  const from = currentRoute?.query?.from as string
  const meta = getCurrentMeta()
  const userStore = getUserStore()

  // 如果是公共页面且不允许通过，或者是微应用场景，直接返回
  if ((meta?.isPublic && !options?.allowPass) || userStore?.isMicroApp) return

  // 根据不同场景处理跳转逻辑
  if (from === "add-more") {
    // 如果是添加更多账号场景，跳转到初始登录页
    redirectWithQuery("/login", { showType: "init" })
  } else if (options?.status === AccountStatus.HAS_CURRENT_ACCOUNT) {
    // 如果已有当前账号，跳转到主应用
    jumpWithRedirect({})
  } else {
    // 其他情况跳转到账号检查页面
    const currentPath = currentRoute?.path
    if (currentPath !== "/check_account") {
      // 避免重复跳转到检查页面
      redirectWithQuery("/check_account")
    } else {
      $logger.log("[accounts]已在检查页面，跳过重复跳转")
    }
  }
}

// 提供一个初始化监听的函数
export function initAuthKitListeners() {
  $authKit.on(AuthEventType.STATUS_CHANGED, async (option?: { status: AccountStatus }) => {
    $logger.log("[accounts]检测到账号状态变化", option)
    handleTabStatus()
  })
  // ...其他监听
}
/** 处理账号切换事件 */
export const handleAccountSwitched = async (opts: AuthEvents[AuthEventType.ACCOUNT_SWITCHED]) => {
  const { userInfo, fromTab } = opts
  await router.isReady() // 等待路由准备好
  const meta = getCurrentMeta()
  $logger.log("[accounts]监听账号切换事件", { orgGuid: userInfo.orgGuid, fromTab, meta })
  message.success(`${i18n.$t("账号登录成功")}～`)
  if (meta?.autoClose) {
    setTimeout(() => {
      if (!window.__SSO_JUMP_URL__) window.close()
    }, 1000)
    return
  }
  jumpWithRedirect({})
}
// 监听选择了账号
$authKit.on(AuthEventType.ACCOUNT_SWITCHED, handleAccountSwitched)
// 监听账号列表变化
let preAccountList: AccountInfo[] = []
$authKit.on(AuthEventType.ACCOUNT_LIST_UPDATED, opts => {
  const { accountList, isTabSync } = opts
  if (accountList.length === preAccountList.length) return
  $logger.log("[accounts]本地账号列表变化", {
    isTabSync: !!isTabSync,
    accountList: accountList.map(item => pick(item, [
      "memberGuid",
      "userName",
      "orgName",
      "token",
      "traceId",
    ])),
    preAccountList: preAccountList.map(item => pick(item, [
      "memberGuid",
      "userName",
      "orgName",
      "token",
      "traceId",
    ])),
  }, { immediate: true })
  preAccountList = accountList
})
export const checkAccountStatus = () => {
  $authKit.isAllLoaded().then(async () => {
    const state = $authKit.getState()
    const status = $authKit.status
    $logger.log("[accounts]账号状态检测完成", { status, accountList: state.accountList }, { immediate: true })
    const userStore = getUserStore()
    if (!userStore) return
    // 防止循环跳转：如果当前已在 check_account 页面，直接处理不跳转
    // $logger.log("[accounts]当前在检查页面，直接处理账号状态", { currentPath, status, failedList: toRaw(userStore.failedList) })
    if (status === AccountStatus.NO_ACCOUNT && userStore.failedList.length === 0) {
      redirectWithQuery("login", { showType: "init" })
    } else {
      redirectWithQuery("login", { showType: "organization" })
      if (window.location.pathname === `${__BASE_PATH__}/check_account`) return
      const memberGuid = getParamsFromUrl(window.location.href).memberGuid
      if (!memberGuid) return
      $logger.log("[accounts]账号列表获取到 memberGuid", memberGuid)

      if (userStore.showType !== "organization") return
      const org = state.accountList.find(item => item.memberGuid === memberGuid) || userStore.failedList.find(item => item.memberGuid === memberGuid)
      if (!org) return
      const scene = (() => {
        switch (org.loginStatus) {
          case AccountLoginStatus.PASSWORD_ERROR:
            return "password-login-password-error"
          case AccountLoginStatus.NOT_SET_PASSWORD:
            return "password-login-set-password"
          default:
            return "login"
        }
      })()

      userStore.updateState({
        currentOrg: org as JoinAccount,
        currentScene: scene,
      })
    }
  })
}
